import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, ShieldCheck, Sparkles, Plus } from 'lucide-react';

const API = '/api';

/**
 * Category Spotlight — paid placement above the listings grid on /business/:slug.
 *
 * • Up to 3 active sponsors per category, randomised by hour (server-side rotation).
 * • If <3 slots are taken, an "Promote Your Business Here" CTA card fills the gap.
 * • Marked rel="sponsored" on outbound links for SEO compliance.
 * • Hidden entirely if `categorySlug` is empty or there's nothing to show *and*
 *   the user is not signed in (avoids cluttering the page for casual browsers).
 */
const CategorySpotlight = ({ categorySlug, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categorySlug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/category-sponsorships/${categorySlug}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  if (!categorySlug || loading) return null;
  // Hide entirely if admin disabled this category for spotlights.
  if (data && data.enabled === false) return null;

  const sponsors = data?.sponsors || [];
  const maxSlots = data?.max_slots || 2;

  // Derive a human-readable name from slug (used in aria-labels)
  const prettyCategory = categorySlug
    .replace(/-and-/g, ' & ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Compact mode: show flat horizontal rows instead of card grid.
  // No empty-slot CTAs, no mobile rotation — show all available sponsors.
  if (compact) {
    if (sponsors.length === 0) return null;
    return (
      <section
        className="mb-4 bg-amber-50/60 border border-amber-200/70 rounded-xl overflow-hidden"
        data-testid="category-spotlight-compact"
        aria-label={`Spotlight sponsors in ${prettyCategory}`}
      >
        <div className="px-3 pt-2.5 pb-0.5 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Spotlight</span>
        </div>
        {sponsors.slice(0, maxSlots).map((s) => (
          <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 border-t border-amber-200/50">
            {/* Thumbnail */}
            {s.image_url ? (
              <img
                src={s.image_url}
                alt={s.title}
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0 ring-1 ring-amber-200"
                loading="lazy"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 text-lg" aria-hidden="true">⭐</div>
            )}
            {/* Name + Town */}
            <div className="flex-1 min-w-0">
              <Link
                to={`/listing/${s.listing_id}`}
                rel="sponsored"
                className="font-medium text-sm text-slate-900 hover:text-blue-700 truncate block leading-tight"
                data-testid={`spotlight-compact-${s.listing_id}`}
              >
                {s.title}
              </Link>
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{s.town || 'Essex'}</span>
              </div>
            </div>
            {/* Sponsored badge */}
            <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-200/80 text-amber-900 px-2 py-0.5 rounded-full flex-shrink-0">
              Sponsored
            </span>
            {/* Promote link */}
            <Link
              to={`/promote/${categorySlug}`}
              className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap flex-shrink-0 font-medium"
              data-testid="spotlight-compact-promote"
            >
              Promote →
            </Link>
          </div>
        ))}
      </section>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const mobileMaxSlots = 1;

  let visibleSponsors = sponsors;
  let effectiveMaxSlots = maxSlots;

  if (isMobile && sponsors.length > 1) {
    // Rotate: pick 1 sponsor based on the current minute for even distribution
    const rotationIndex = Math.floor(Date.now() / 60000) % sponsors.length;
    visibleSponsors = [sponsors[rotationIndex]];
    effectiveMaxSlots = mobileMaxSlots;
  } else if (isMobile) {
    visibleSponsors = sponsors.slice(0, 1);
    effectiveMaxSlots = mobileMaxSlots;
  }

  const emptySlots = Math.max(0, effectiveMaxSlots - visibleSponsors.length);

  // Show the section even when zero sponsors so businesses can discover the
  // promote CTA. Admin can disable per-category via Pricing Config if they
  // want a category hidden entirely.
  const hasAnything = visibleSponsors.length > 0 || emptySlots > 0;
  if (!hasAnything) return null;

  return (
    <section
      className="mb-6 md:mb-8"
      data-testid="category-spotlight"
      aria-label={`Spotlight sponsors in ${prettyCategory}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
          Spotlight in {prettyCategory}
        </h2>
        <Link
          to={`/promote/${categorySlug}`}
          className="text-xs md:text-sm text-blue-700 hover:text-blue-900 font-medium hover:underline whitespace-nowrap"
          data-testid="spotlight-promote-link"
        >
          Promote your business →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {visibleSponsors.map((s) => (
          <Link
            key={s.id}
            to={`/listing/${s.listing_id}`}
            rel="sponsored"
            data-testid={`spotlight-card-${s.listing_id}`}
            className="group relative bg-gradient-to-br from-amber-50 via-white to-amber-50 border-2 border-amber-300/60 hover:border-amber-400 rounded-2xl p-4 md:p-5 transition-all hover:shadow-lg hover:-translate-y-0.5"
          >
            <span className="absolute top-2.5 right-2.5 text-[10px] uppercase tracking-wide font-semibold bg-amber-400/90 text-amber-950 px-2 py-0.5 rounded-full">
              Sponsored
            </span>

            <div className="flex items-start gap-3">
              {s.image_url ? (
                <img
                  src={s.image_url}
                  alt={s.title}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover ring-2 ring-white shadow-sm shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-amber-100 flex items-center justify-center text-2xl shrink-0">
                  ⭐
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 text-sm md:text-base leading-tight truncate group-hover:text-blue-700 transition-colors">
                  {s.title}
                </h3>
                <div className="flex items-center gap-1 text-xs text-slate-600 mt-1">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{s.town || 'Essex'}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  {s.rating ? (
                    <span className="flex items-center gap-0.5 text-xs font-medium text-amber-700">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {Number(s.rating).toFixed(1)}
                      {s.review_count ? (
                        <span className="text-slate-400 ml-0.5">({s.review_count})</span>
                      ) : null}
                    </span>
                  ) : null}
                  {s.verified ? (
                    <span className="flex items-center gap-0.5 text-xs text-blue-700">
                      <ShieldCheck className="h-3 w-3" />
                      Verified
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </Link>
        ))}

        {/* Empty-slot CTA cards */}
        {Array.from({ length: emptySlots }).map((_, i) => (
          <Link
            key={`empty-${i}`}
            to={`/promote/${categorySlug}`}
            data-testid={`spotlight-empty-slot-${i}`}
            className="group relative border-2 border-dashed border-slate-300 hover:border-amber-400 rounded-2xl p-4 md:p-5 flex flex-col items-center justify-center text-center transition-all hover:bg-amber-50/40 min-h-[100px]"
          >
            <Plus className="h-6 w-6 text-slate-400 group-hover:text-amber-500 transition-colors mb-1.5" />
            <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
              Promote your business here
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              7 – 365 days
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default CategorySpotlight;
